/**
 * Pure helpers used by DiscrepancyReviewDialog. Extracted here so they can
 * be unit-tested without pulling in the full dialog module (Apollo queries,
 * MUI components, etc.).
 *
 * Helpers accept structural types (not the derived GraphQL `Discrepancy`
 * type) so tests can pass minimal fixtures without depending on codegen.
 */
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import SyncIcon from "@mui/icons-material/Sync";
import React from "react";

export type DiscrepancyStatus = "unresolved" | "pending" | "resolved";

/** The complete list of possible DiscrepancyStatus values, in UI order. */
export const ALL_DISCREPANCY_STATUSES: readonly DiscrepancyStatus[] = [
  "unresolved",
  "pending",
  "resolved",
];

/** Type guard: narrows an arbitrary string to a DiscrepancyStatus. */
export function isDiscrepancyStatus(v: string): v is DiscrepancyStatus {
  return v === "unresolved" || v === "pending" || v === "resolved";
}

/** The minimal discrepancy shape the helpers operate on. */
export type DiscrepancyLike = {
  field: string;
  kind?: string | null;
  resolutionPath?: string | null;
  resolvedBy?: unknown;
  escalation?: string | null;
  status?: string | null;
};

// --- Field label map ---

export const FIELD_LABELS: Record<string, string> = {
  generatorName: "Generator Name",
  generatorId: "Generator EPA ID",
  generatorStreetAddress: "Generator Street Address",
  generatorStreet2: "Generator Street 2",
  generatorCity: "Generator City",
  generatorState: "Generator State",
  generatorZip: "Generator ZIP",
  shippedDate: "Ship Date",
  destinationFacilityId: "TSDF EPA ID",
  destinationFacilityName: "TSDF Name",
  wasteLinesCount: "Waste Line Count",
  quantity: "Quantity",
  quantityUnitOfMeasureCode: "UOM Code",
  quantityUnitOfMeasureDescription: "UOM Description",
  containerTypeCode: "Container Type",
  managementMethodCode: "Management Method",
  federalWasteCodes: "Federal Waste Codes",
  stateWasteCodes: "State Waste Codes",
  dotPrintedInformation: "DOT Description",
  dotProperShippingName: "DOT Proper Shipping Name",
  nonHazWasteDescription: "Non-Haz Description",
  transporter1Name: "Transporter 1 Name",
  transporter1Id: "Transporter 1 EPA ID",
  transporter2Name: "Transporter 2 Name",
  transporter2Id: "Transporter 2 EPA ID",
  transporter3Id: "Transporter 3 EPA ID",
  generatorCertification: "Generator Certification",
  transporterCertification: "Transporter Certification",
  tsdfCertification: "TSDF Certification",
  eManifestMissing: "Missing e-Manifest",
  epaCorrectionRequest: "EPA Correction Request",
};

export function getFieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

// --- Status derivation ---

/** A discrepancy is a compliance row if it has an escalation level. */
export function isComplianceDiscrepancy(d: { escalation?: string | null }): boolean {
  return d.escalation != null;
}

export function getDiscrepancyStatus(d: { status?: string | null }): DiscrepancyStatus {
  switch (d.status) {
    case "RESOLVED":
    case "IGNORED":
      return "resolved";
    case "PENDING":
      return "pending";
    default:
      return "unresolved";
  }
}

/** Matches the subset of MUI Chip `color` values we use for status chips. */
export type StatusChipColor = "warning" | "info" | "success";

export function getStatusColor(status: DiscrepancyStatus): StatusChipColor {
  switch (status) {
    case "unresolved":
      return "warning";
    case "pending":
      return "info";
    case "resolved":
      return "success";
  }
}

export function getStatusLabel(status: DiscrepancyStatus) {
  switch (status) {
    case "unresolved":
      return "Unresolved";
    case "pending":
      return "Pending";
    case "resolved":
      return "Resolved";
  }
}

// --- Status chip iconography ---

/**
 * Returns the icon element to render inside the status chip, or `undefined`
 * for a plain (icon-less) chip.
 */
export function getStatusChipIcon(
  d: Pick<DiscrepancyLike, "field" | "kind" | "resolutionPath" | "resolvedBy">,
  status: DiscrepancyStatus
): React.ReactElement<{ "aria-hidden"?: boolean }> | undefined {
  if (d.field === "eManifestMissing") return <ErrorOutlineIcon />;
  // Manual vendor-data fix (a user resolved it locally).
  if (d.resolutionPath === "VENDOR_DATA" && d.resolvedBy) return <CheckIcon />;
  // Resolved at the source / auto-resolved.
  if (status === "resolved") return <PublishedWithChangesIcon />;
  if (status === "pending") return <SyncIcon />;
  return undefined;
}

/**
 * Tooltip text that explains *why* a chip has its current status/icon.
 * The icon alone can't communicate whether a resolved chip was manually
 * refreshed vs auto-resolved by the pipeline, or whether a pending chip is
 * waiting on new vendor data vs an exception report, so surface the detail
 * on hover.
 */
export function getStatusChipTooltip(
  d: Pick<DiscrepancyLike, "field" | "kind" | "resolutionPath" | "resolvedBy" | "escalation">,
  status: DiscrepancyStatus
): string | undefined {
  if (d.field === "eManifestMissing") {
    return "No matching e-Manifest record found for this manifest";
  }

  if (status === "resolved") {
    if (d.kind === "EPA_CORRECTION_REQUEST") {
      return "Corrected in e-Manifest";
    }
    if (d.resolutionPath === "EMANIFEST") {
      return "Corrected in e-Manifest";
    }
    if (d.resolutionPath === "VENDOR_DATA") {
      return "Vendor data corrected";
    }
    return "Auto-resolved — values now match";
  }

  if (status === "pending") {
    if (d.escalation === "OVERDUE_60") return "Pending exception report";
    // Data could flow back from either side (vendor re-sync or eManifest
    // update in RCRAInfo) — stay source-agnostic.
    return "Pending data refresh";
  }

  if (status === "unresolved") {
    return "Needs review";
  }

  return undefined;
}

// --- Value formatting ---

/** Extract just the date portion (YYYY-MM-DD) from an ISO string. */
export function formatDateOnly(value: string | null | undefined): string {
  if (value == null) return "—";
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : value;
}

/**
 * Strip JSON-ish quoting/bracket artifacts from a stored string value so it
 * renders cleanly. Stored values sometimes look like `"D001"` or
 * `["D001", "D002"]`; we want `D001` and `D001, D002` respectively.
 * `null` and `"[]"` render as an em dash placeholder.
 */
export function formatDisplayValue(value: string | null | undefined): string {
  if (value == null) return "—";
  if (value === "[]") return "—";
  const stripped = value.replace(/^"(.*)"$/, "$1").replace(/"\s*,\s*"/g, ", ");
  const unbracketed = stripped.replace(/^\[(.*)\]$/, "$1").replace(/"/g, "");
  return unbracketed || "—";
}

/**
 * Determines which badge case applies to a field discrepancy row based on
 * which sources differ from the eManifest value.
 *
 * - `"vendor-only"`: only the vendor value differs
 * - `"doc-only"`: only the document value differs
 * - `"both"`: vendor and document both differ (worst case)
 * - `null`: no displayable badge (neither present or both match)
 */
export type DiscrepancyBadgeCase = "vendor-only" | "doc-only" | "both" | null;

export function getDiscrepancyBadgeCase(row: {
  eManifestValue?: string | null;
  vendorValue?: string | null;
  manifestDocumentValue?: string | null;
  vendorDiscrepant?: boolean | null;
  documentDiscrepant?: boolean | null;
}): DiscrepancyBadgeCase {
  // Prefer the persisted per-axis verdicts (computed by the normalization-aware
  // comparators). Fall back to raw value comparison only for legacy rows that
  // predate those columns (verdict === null), where raw inequality is the best
  // signal available until the row is next recomputed. Raw comparison ignores
  // field-specific normalization (zip, EPA-ID name match, case), so it can show
  // a spurious chip — the verdict columns exist precisely to stop that.
  const vendorDiffers =
    row.vendorDiscrepant ?? (row.vendorValue != null && row.vendorValue !== row.eManifestValue);
  const docDiffers =
    row.documentDiscrepant ??
    (row.manifestDocumentValue != null && row.manifestDocumentValue !== row.eManifestValue);
  if (vendorDiffers && docDiffers) return "both";
  if (vendorDiffers) return "vendor-only";
  if (docDiffers) return "doc-only";
  return null;
}
